import type { AutoCompleteResult } from '@awazevr/design-system.components.autocomplete'
import {
  DataLayerEventType,
  DataLayerFactory,
  type TmsContextType,
} from '@awazevr/mfe-common.contexts.tms'

import { OPTIMIZELY_EVENTS } from '@/constants'
import type { OptimizelyState } from '@/context/optimizely-decisions-context'
import { dataLayerErrorHandler } from '@/utils/dataLayerErrorHandler'
import { trackClientSideOptimizelyEvent } from '@/utils/optimizely/trackOptimizelyEvent'

import type { PlacesSuggestions, Suggestion } from './SearchForm.types'

const fireLocationSuggestionOptimizelyEvent = (
  placesResult: Suggestion[],
  optimizelyState?: OptimizelyState,
): void => {
  if (!optimizelyState) {
    return
  }
  const hasInternalResults =
    placesResult.length > 0 && !placesResult[0].type?.includes('GooglePlaces')
  trackClientSideOptimizelyEvent(
    hasInternalResults
      ? OPTIMIZELY_EVENTS.INTERNAL_LOCATION_SUGGESTION_SHOWN
      : OPTIMIZELY_EVENTS.INTERNAL_LOCATION_SUGGESTION_NO_RESULTS,
    optimizelyState,
  )
}

type OriginalAutoSuggestSearch = (
  searchTerm: string,
  accommodationType: string,
  propertiesOnly: boolean,
) => Promise<AutoCompleteResult[]>

const fireAutoSuggestTrackingEvents = ({
  searchTerm,
  accommodationType,
  destinations,
  properties,
  noResult,
  tms,
}: {
  searchTerm: string
  accommodationType?: string
  destinations: string[]
  properties: string[]
  noResult: boolean
  tms?: TmsContextType
}): void => {
  if (!tms) {
    return
  }

  if (noResult) {
    tms.updateDataLayer('link', 'auto_suggest_noresult', {
      search_term: searchTerm,
    })
    return
  }

  new DataLayerFactory()
    .getEvent(DataLayerEventType.AutoSuggestImpression)
    .update(
      tms,
      {
        searchTerm,
        accommodationType,
        destinations,
        properties,
      },
      dataLayerErrorHandler,
    )
}

export const combineAutoSuggestSearch = (
  getSuggestions: PlacesSuggestions,
  originalAutoSuggestSearch: OriginalAutoSuggestSearch,
  tms?: TmsContextType,
  useSuggestionsRestEndpoint = false,
  optimizelyState?: OptimizelyState,
): ((
  searchTerm: string | null,
  accommodationType?: string,
) => Promise<AutoCompleteResult[]>) => {
  return async (
    searchTerm: string | null,
    accommodationType?: string,
  ): Promise<AutoCompleteResult[]> => {
    if (!searchTerm) {
      return []
    }

    // Skip Places lookup entirely for boat-holidays — only use location lookup
    const forceLocationLookupIfBoats = accommodationType === 'boat-holidays'

    if (forceLocationLookupIfBoats) {
      const originalResult = await originalAutoSuggestSearch(
        searchTerm,
        accommodationType,
        false,
      )

      fireAutoSuggestTrackingEvents({
        searchTerm,
        accommodationType,
        destinations: originalResult
          .filter((result) => !result.property)
          .map(({ name }) => name),
        properties: originalResult
          .filter((result) => result.property)
          .map(({ name }) => name),
        noResult: originalResult.length === 0,
        tms,
      })

      return originalResult
    }

    const [placesResult, originalPropertyResults] = useSuggestionsRestEndpoint
      ? [await getSuggestions(searchTerm, accommodationType), []]
      : await Promise.all([
          getSuggestions(searchTerm, accommodationType),
          originalAutoSuggestSearch(
            searchTerm,
            accommodationType as string,
            true,
          ),
        ])
    const destinationResults = placesResult.filter((r) => !r.property)
    const propertyResults = useSuggestionsRestEndpoint
      ? placesResult.filter((r) => r.property)
      : originalPropertyResults.filter((r) => r.property)
    const autoSuggestResults = [...destinationResults, ...propertyResults]

    fireAutoSuggestTrackingEvents({
      searchTerm,
      accommodationType,
      destinations: destinationResults.map(({ name }) => name),
      properties: propertyResults.map(({ name }) => name),
      noResult: autoSuggestResults.length === 0,
      tms,
    })

    fireLocationSuggestionOptimizelyEvent(destinationResults, optimizelyState)

    return autoSuggestResults
  }
}
